import { Component, ChangeDetectionStrategy, ChangeDetectorRef, inject, input, output, signal } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, FormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { PERMISSION_UI_DIRECTIVES } from '../../directives/permission.directive';
import { InputTextModule } from 'primeng/inputtext';
import { FloatLabelModule } from 'primeng/floatlabel';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TabsModule } from 'primeng/tabs';
import {
  CustomerService,
  CreateCustomerRequest,
  UpdateCustomerRequest,
  CustomerDTO,
  CustomerAddressDTO,
  CustomerContactDTO,
  CreateCustomerAddressRequest,
  CreateCustomerContactRequest,
  ReadFileResponse
} from '../../services/customer.service';
import { CountryService } from '../../services/country.service';
import type { CountryDTO } from '../../services/country.service';
import { AddressService } from '../../services/address.service';
import { ToastrService } from 'ngx-toastr';
import { combineLatest, finalize, of, catchError } from 'rxjs';
import { InputMaskModule } from 'primeng/inputmask';
import { InputNumberModule } from 'primeng/inputnumber';
import { TaxRegimeService } from '../../services/tax-regime.service';
import type { TaxRegimeDTO } from '../../services/tax-regime.service';
import { CompanyService } from '../../services/company.service';
import { CompanyTypeOptions, type CompanyTypeEnumValue } from '../../core/models';
import { TooltipModule } from 'primeng/tooltip';
import { EntityAuditPanelComponent } from '../shared/entity-audit/entity-audit-panel.component';

@Component({
  selector: 'app-customer-form',
  templateUrl: './customer-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  styles: [
    `
      :host ::ng-deep .sale-form-card.p-card .p-card-header {
        padding: 0;
      }
      :host ::ng-deep .sale-form-card.p-card .p-card-body {
        padding-top: 0.25rem;
      }
      :host ::ng-deep .sale-form-card .p-tablist,
      :host ::ng-deep .sale-form-card .p-tabview-tablist,
      :host ::ng-deep .sale-form-card .p-tabs-nav,
      :host ::ng-deep .sale-form-card [data-pc-section='tablist'] {
        padding-top: 0;
        margin-top: 0;
      }
      :host ::ng-deep .sale-form-card .p-tabpanels,
      :host ::ng-deep .sale-form-card .p-tabpanel {
        padding-top: 0.5rem;
      }
    `
  ],
  imports: [
    CommonModule,
    PERMISSION_UI_DIRECTIVES,
    ReactiveFormsModule,
    InputTextModule,
    FloatLabelModule,
    SelectModule,
    ButtonModule,
    CardModule,
    TabsModule,
    InputMaskModule,
    InputNumberModule,
    FormsModule,
    TooltipModule,
    EntityAuditPanelComponent
  ]
})
export class CustomerFormComponent {
  readonly embedded = input(false);
  readonly saved = output<CustomerDTO>();
  readonly dismissed = output<void>();

  private fb = inject(FormBuilder);
  private customerService = inject(CustomerService);
  private countryService = inject(CountryService);
  private addressService = inject(AddressService);
  private taxRegimeService = inject(TaxRegimeService);
  private companyService = inject(CompanyService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private toastr = inject(ToastrService);
  private cdr = inject(ChangeDetectorRef);

  customerForm: FormGroup;
  loading = signal(false);
  /** Overlay de carga inicial o refresh del cliente (no guardado ni otras acciones). */
  formDataLoading = signal(false);
  error = signal<string | null>(null);
  isEditMode = signal(false);
  editingCustomerId = signal<number | null>(null);
  reactivating = signal(false);
  addresses = signal<CustomerAddressDTO[]>([]);
  contacts = signal<CustomerContactDTO[]>([]);
  attachments = signal<ReadFileResponse[]>([]);
  attachmentsLoading = signal(false);
  uploadingAttachments = signal(false);
  addressForm: FormGroup;
  contactForm: FormGroup;
  editingAddressId = signal<number | null>(null);
  editingContactId = signal<number | null>(null);
  showAddressForm = signal(false);
  showContactForm = signal(false);
  addressModalShown = signal(false);
  contactModalShown = signal(false);
  relationsLoading = signal(false);
  deleteConfirmOpen = signal(false);
  deleteConfirmShown = signal(false);
  deleteTarget = signal<'address' | 'contact' | 'attachment' | null>(null);
  deleteAddressItem = signal<CustomerAddressDTO | null>(null);
  deleteContactItem = signal<CustomerContactDTO | null>(null);
  deleteAttachmentItem = signal<ReadFileResponse | null>(null);
  deleteLoading = signal(false);

  readonly CompanyTypeOptions = CompanyTypeOptions;

  addressTypes = [
    { value: 'BILLING', label: 'Facturación' },
    { value: 'PRIMARY', label: 'Principal' },
    { value: 'SHIPPING', label: 'Envío' },
    { value: 'CONTACT', label: 'Contacto' },
    { value: 'OTHER', label: 'Otro' }
  ];

  contactTypes = [
    { value: 'PRIMARY', label: 'Principal' },
    { value: 'OFFICE', label: 'Oficina' },
    { value: 'HOME', label: 'Casa' },
    { value: 'MOBILE', label: 'Móvil' },
    { value: 'FAX', label: 'Fax' },
    { value: 'EMAIL', label: 'Email' },
    { value: 'WEBSITE', label: 'Sitio Web' },
    { value: 'SOCIAL_MEDIA', label: 'Red Social' },
    { value: 'OTHER', label: 'Otro' }
  ];

  countries = signal<CountryDTO[]>([]);
  taxRegimeOptions = signal<TaxRegimeDTO[]>([]);
  coloniaOptions = signal<{ label: string; value: string }[]>([]);
  postalCodeLoading = signal(false);
  mexicoSelected = signal(true);
  municipalityDisplay = signal('');
  /** Plazo de pago por defecto de la empresa (GET /companies/me). */
  companyDefaultDaysUntilExpiry = signal<number | null>(null);

  stateOptions = [
    { value: 'ACTIVE', label: 'Activo' },
    { value: 'INACTIVE', label: 'Inactivo' }
  ];

  activeTab = signal<string>('generales');

  tabs = [
    { value: 'generales', label: 'Generales', icon: 'pi pi-user' },
    { value: 'direcciones', label: 'Direcciones', icon: 'pi pi-map-marker' },
    { value: 'contactos', label: 'Contactos', icon: 'pi pi-users' },
    { value: 'adjuntos', label: 'Adjuntos', icon: 'pi pi-paperclip' },
    { value: 'auditoria', label: 'Auditoría', icon: 'pi pi-history' }
  ] as const;

  /**
   * Longitudes máximas alineadas con las entidades JPA:
   * Customer, CustomerAddress, CustomerContact.
   * contactPhoneMasked: longitud del valor con máscara "(999) 999-9999" (10 dígitos en DB).
   */
  readonly maxLength = {
    taxId: 20,
    legalName: 200,
    comercialName: 200,
    email: 100,
    phone: 10,
    country: 100,
    street: 200,
    municipality: 100,
    colonia: 100,
    externalNo: 30,
    internalNo: 30,
    zipCode: 5,
    contactName: 100,
    contactEmail: 100,
    contactPhone: 10,
    contactPhoneMasked: 10,
    daysUntilExpiry: 3
  } as const;

  private static readonly nonNegativeIntegerValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw === null || raw === undefined || raw === '') {
      return null;
    }
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) {
      return { nonNegativeInteger: true };
    }
    return null;
  };

  private static readonly maxDigitsValidator = (maxDigits: number): ValidatorFn => {
    return (control: AbstractControl): ValidationErrors | null => {
      const raw = control.value;
      if (raw === null || raw === undefined || raw === '') {
        return null;
      }
      const digits = String(raw).replace(/\D/g, '');
      if (digits.length > maxDigits) {
        return { maxlength: { requiredLength: maxDigits, actualLength: digits.length } };
      }
      return null;
    };
  };

  /** Formato email estricto (local@dominio.tld). Vacío permitido. */
  private static readonly emailFormatValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw === null || raw === undefined || raw === '') {
      return null;
    }
    const email = String(raw).trim();
    if (!email) {
      return null;
    }
    const EMAIL_REGEX =
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
    return EMAIL_REGEX.test(email) ? null : { email: true };
  };

  constructor() {
    const ml = this.maxLength;
    this.customerForm = this.fb.group({
      type: [null as CompanyTypeEnumValue | null],
      taxRegimeId: [null as number | null],
      taxId: ['', [Validators.maxLength(ml.taxId)]],
      legalName: ['', [Validators.required, Validators.maxLength(ml.legalName)]],
      comercialName: ['', [Validators.required, Validators.maxLength(ml.comercialName)]],
      countryId: [null as number | null],
      email: ['', [Validators.required, CustomerFormComponent.emailFormatValidator, Validators.maxLength(ml.email)]],
      phone: ['', [Validators.maxLength(ml.phone), Validators.pattern(/^$|^\d{10}$/)]],
      state: ['ACTIVE'],
      daysUntilExpiry: [null as number | null, [
        CustomerFormComponent.nonNegativeIntegerValidator,
        CustomerFormComponent.maxDigitsValidator(ml.daysUntilExpiry)
      ]]
    });

    this.addressForm = this.fb.group({
      addressType: ['BILLING', Validators.required],
      addressCountry: ['', [Validators.required, Validators.maxLength(5)]],
      street: ['', [Validators.required, Validators.maxLength(ml.street)]],
      zipCode: ['', [Validators.required, Validators.pattern(/^\d{5}$/)]],
      municipality: ['', [Validators.required, Validators.maxLength(ml.municipality)]],
      colonia: ['', [Validators.required, Validators.maxLength(ml.colonia)]],
      externalNo: ['', [Validators.required, Validators.maxLength(ml.externalNo)]],
      internalNo: ['', [Validators.maxLength(ml.internalNo)]]
    });

    this.contactForm = this.fb.group({
      contactType: ['PRIMARY', Validators.required],
      conactName: ['', [Validators.required, Validators.maxLength(ml.contactName)]],
      contactEmail: ['', [CustomerFormComponent.emailFormatValidator, Validators.maxLength(ml.contactEmail)]],
      contactPhone: ['', [Validators.maxLength(ml.contactPhoneMasked), Validators.pattern(/^\d*$/)]]
    });

    this.addressForm.get('addressCountry')?.valueChanges.subscribe((code) => {
      const isMexico = this.isMexicoCountryCode(code);
      this.mexicoSelected.set(isMexico);
      if (!isMexico) {
        this.coloniaOptions.set([]);
      }
      this.cdr.markForCheck();
    });

    this.countryService.getCountries({}, { page: 0, size: 300 }).subscribe({
      next: (r) => this.countries.set(r.body ?? []),
      error: () => this.countries.set([])
    });

    this.taxRegimeService.getAllTaxRegimes().subscribe({
      next: (r) => this.taxRegimeOptions.set(r.body ?? []),
      error: () => this.taxRegimeOptions.set([])
    });

    this.loadCompanyDefaultPaymentTerms();
  }

  ngOnInit(): void {
    if (this.embedded()) {
      return;
    }

    combineLatest([this.route.paramMap, this.route.queryParamMap]).subscribe(([params, query]) => {
      const routeId = params.get('id');
      const queryId = query.get('id');
      const idParam = routeId || queryId;

      if (idParam) {
        const customerId = Number(idParam);
        if (!Number.isNaN(customerId)) {
          this.isEditMode.set(true);
          this.editingCustomerId.set(customerId);
          this.loadCustomer(customerId);
          return;
        }
      }

      this.isEditMode.set(false);
      this.editingCustomerId.set(null);
      this.customerForm.reset({
        type: null,
        taxRegimeId: null,
        taxId: '',
        legalName: '',
        comercialName: '',
        countryId: null,
        email: '',
        phone: '',
        state: 'ACTIVE',
        daysUntilExpiry: null
      });
      this.addresses.set([]);
      this.contacts.set([]);
      this.attachments.set([]);
      this.showAddressForm.set(false);
      this.showContactForm.set(false);
    });
  }

  private findMexicoCountry(countries: CountryDTO[]): CountryDTO | undefined {
    return countries.find((c) => this.isMexicoCountryCode(c.code) || this.isMexicoCountryName(c.name));
  }

  private findMexicoCountryCode(countries: CountryDTO[] = this.countries()): string {
    return this.findMexicoCountry(countries)?.code ?? 'MX';
  }

  isMexicoCountryCode(code: string | null | undefined): boolean {
    const normalized = (code ?? '').toUpperCase();
    return normalized === 'MX' || normalized === 'MEX';
  }

  private isMexicoCountryName(name: string | null | undefined): boolean {
    const normalized = (name ?? '').toLowerCase();
    return normalized === 'méxico' || normalized === 'mexico';
  }

  isMexicoSelected(): boolean {
    return this.mexicoSelected();
  }

  private emptyAddressFormValue(): Record<string, string> {
    const country = this.findMexicoCountryCode();
    this.mexicoSelected.set(this.isMexicoCountryCode(country));
    this.municipalityDisplay.set('');
    return {
      addressType: 'BILLING',
      addressCountry: country,
      street: '',
      zipCode: '',
      municipality: '',
      colonia: '',
      externalNo: '',
      internalNo: ''
    };
  }

  private refreshMunicipalityDisplay(): void {
    this.municipalityDisplay.set(String(this.addressForm.get('municipality')?.value ?? '').trim());
  }

  formatAddressStreet(address: CustomerAddressDTO): string {
    const parts = [
      address.street,
      address.externalNo ? `Ext. ${address.externalNo}` : '',
      address.internalNo ? `Int. ${address.internalNo}` : '',
      address.colonia
    ].filter((part) => !!part && String(part).trim().length > 0);
    return parts.length ? parts.join(', ') : '-';
  }

  formatAddressLocality(address: CustomerAddressDTO): string {
    const parts = [
      address.municipality || '-',
      address.zipCode || ''
    ].filter((part) => !!part && String(part).trim().length > 0);
    return parts.join(', ');
  }

  onLegalNameBlur(): void {
    const legalName = (this.customerForm.get('legalName')?.value ?? '').trim();
    const comercialName = (this.customerForm.get('comercialName')?.value ?? '').trim();
    if (legalName && !comercialName) {
      this.customerForm.get('comercialName')?.setValue(legalName);
    }
  }

  startEditAddress(address: CustomerAddressDTO): void {
    this.editingAddressId.set(address.id);
    this.showAddressForm.set(true);
    this.addressModalShown.set(false);
    const colonia = address.colonia || '';
    this.coloniaOptions.set(colonia ? [{ label: colonia, value: colonia }] : []);
    const countryCode = address.country?.code || this.findMexicoCountryCode();
    this.mexicoSelected.set(this.isMexicoCountryCode(countryCode));
    this.addressForm.patchValue({
      addressType: address.type,
      addressCountry: countryCode,
      street: address.street || '',
      zipCode: address.zipCode || '',
      municipality: address.municipality || '',
      colonia,
      externalNo: address.externalNo || '',
      internalNo: address.internalNo || ''
    });
    this.refreshMunicipalityDisplay();
    setTimeout(() => this.addressModalShown.set(true), 10);
  }

  startEditContact(contact: CustomerContactDTO): void {
    this.editingContactId.set(contact.id);
    this.showContactForm.set(true);
    this.contactModalShown.set(false);
    this.contactForm.patchValue({
      contactType: contact.type,
      conactName: contact.conactName,
      contactEmail: contact.email || '',
      contactPhone: contact.phone || ''
    });
    setTimeout(() => this.contactModalShown.set(true), 10);
  }

  cancelAddressEdit(): void {
    this.addressModalShown.set(false);
    setTimeout(() => {
      this.editingAddressId.set(null);
      this.showAddressForm.set(false);
      this.coloniaOptions.set([]);
      this.addressForm.reset(this.emptyAddressFormValue());
    }, 200);
  }

  cancelContactEdit(): void {
    this.contactModalShown.set(false);
    setTimeout(() => {
      this.editingContactId.set(null);
      this.showContactForm.set(false);
      this.contactForm.reset({
        type: 'PRIMARY',
        conactName: '',
        email: '',
        phone: ''
      });
    }, 200);
  }

  newAddress(): void {
    this.editingAddressId.set(null);
    this.showAddressForm.set(true);
    this.addressModalShown.set(false);
    this.coloniaOptions.set([]);
    this.addressForm.reset(this.emptyAddressFormValue());
    setTimeout(() => this.addressModalShown.set(true), 10);
  }

  newContact(): void {
    this.editingContactId.set(null);
    this.showContactForm.set(true);
    this.contactModalShown.set(false);
    this.contactForm.reset({
      contactType: 'PRIMARY',
      conactName: '',
      contactEmail: '',
      contactPhone: ''
    });
    setTimeout(() => this.contactModalShown.set(true), 10);
  }

  getAddressTypeLabel(type: string): string {
    if (!type) return '';
    return this.addressTypes.find(o => o.value === type)?.label ?? type;
  }

  getAddressTypeIcon(type: string): string {
    const icons: Record<string, string> = {
      PRIMARY: 'pi-home',
      BILLING: 'pi-credit-card',
      SHIPPING: 'pi-truck',
      CONTACT: 'pi-user',
      OTHER: 'pi-map-marker'
    };
    return icons[type ?? ''] ?? 'pi-map-marker';
  }

  getContactTypeLabel(type: string): string {
    if (!type) return '';
    return this.contactTypes.find(o => o.value === type)?.label ?? type;
  }

  getContactTypeIcon(type: string): string {
    const icons: Record<string, string> = {
      PRIMARY: 'pi-user',
      OFFICE: 'pi-building',
      HOME: 'pi-home',
      MOBILE: 'pi-phone',
      FAX: 'pi-print',
      EMAIL: 'pi-envelope',
      WEBSITE: 'pi-globe',
      SOCIAL_MEDIA: 'pi-share-alt',
      OTHER: 'pi-ellipsis-v'
    };
    return icons[type ?? ''] ?? 'pi-user';
  }

  openDeleteAddress(address: CustomerAddressDTO): void {
    this.deleteTarget.set('address');
    this.deleteAddressItem.set(address);
    this.deleteContactItem.set(null);
    this.deleteAttachmentItem.set(null);
    this.deleteConfirmOpen.set(true);
    this.deleteConfirmShown.set(false);
    setTimeout(() => this.deleteConfirmShown.set(true), 10);
  }

  openDeleteContact(contact: CustomerContactDTO): void {
    this.deleteTarget.set('contact');
    this.deleteContactItem.set(contact);
    this.deleteAddressItem.set(null);
    this.deleteAttachmentItem.set(null);
    this.deleteConfirmOpen.set(true);
    this.deleteConfirmShown.set(false);
    setTimeout(() => this.deleteConfirmShown.set(true), 10);
  }

  openDeleteAttachment(file: ReadFileResponse): void {
    this.deleteTarget.set('attachment');
    this.deleteAttachmentItem.set(file);
    this.deleteAddressItem.set(null);
    this.deleteContactItem.set(null);
    this.deleteConfirmOpen.set(true);
    this.deleteConfirmShown.set(false);
    setTimeout(() => this.deleteConfirmShown.set(true), 10);
  }

  closeDeleteConfirm(): void {
    this.deleteConfirmShown.set(false);
    setTimeout(() => {
      this.deleteTarget.set(null);
      this.deleteAddressItem.set(null);
      this.deleteContactItem.set(null);
      this.deleteAttachmentItem.set(null);
      this.deleteConfirmOpen.set(false);
    }, 200);
  }

  confirmDelete(): void {
    const customerId = this.editingCustomerId();
    if (!customerId) return;
    const target = this.deleteTarget();
    const addressItem = this.deleteAddressItem();
    const contactItem = this.deleteContactItem();
    const attachmentItem = this.deleteAttachmentItem();
    this.deleteLoading.set(true);
    if (target === 'address' && addressItem) {
      this.customerService.deleteCustomerAddress(customerId, addressItem.id).pipe(
        finalize(() => this.deleteLoading.set(false))
      ).subscribe({
        next: (res) => {
          if (res?.ok !== false) {
            this.addresses.update(list => list.filter(a => a.id !== addressItem.id));
            this.closeDeleteConfirm();
            this.toastr.success('Dirección eliminada correctamente');
          } else {
            this.toastr.error(res?.message ?? 'Error al eliminar la dirección');
          }
        },
        error: (err) => {
          this.toastr.error(err.error?.message ?? 'Error al eliminar la dirección');
        }
      });
    } else if (target === 'contact' && contactItem) {
      this.customerService.deleteCustomerContact(customerId, contactItem.id).pipe(
        finalize(() => this.deleteLoading.set(false))
      ).subscribe({
        next: (res) => {
          if (res?.ok !== false) {
            this.contacts.update(list => list.filter(c => c.id !== contactItem.id));
            this.closeDeleteConfirm();
            this.toastr.success('Contacto eliminado correctamente');
          } else {
            this.toastr.error(res?.message ?? 'Error al eliminar el contacto');
          }
        },
        error: (err) => {
          this.toastr.error(err.error?.message ?? 'Error al eliminar el contacto');
        }
      });
    } else if (target === 'attachment' && attachmentItem?.id != null) {
      this.customerService.deleteCustomerAttachment(customerId, attachmentItem.id).pipe(
        finalize(() => this.deleteLoading.set(false))
      ).subscribe({
        next: (res) => {
          if (res?.ok !== false) {
            this.attachments.update(list => list.filter(f => f.id !== attachmentItem.id));
            this.closeDeleteConfirm();
            this.toastr.success('Archivo eliminado correctamente');
          } else {
            this.toastr.error(res?.message ?? 'Error al eliminar el archivo');
          }
        },
        error: (err) => {
          this.toastr.error(err.error?.message ?? 'Error al eliminar el archivo');
        }
      });
    } else {
      this.deleteLoading.set(false);
    }
  }

  saveAddress(): void {
    const customerId = this.editingCustomerId();
    if (!customerId || this.addressForm.invalid) {
      this.addressForm.markAllAsTouched();
      this.cdr.markForCheck();
      return;
    }

    this.relationsLoading.set(true);
    this.clearError();
    const v = this.addressForm.value;
    const payload: CreateCustomerAddressRequest = {
      type: v.addressType,
      country: v.addressCountry,
      street: v.street,
      municipality: v.municipality || undefined,
      colonia: v.colonia || undefined,
      externalNo: v.externalNo || undefined,
      internalNo: v.internalNo || undefined,
      zipCode: v.zipCode || undefined
    };
    const addressId = this.editingAddressId();
    const request$ = addressId
      ? this.customerService.updateCustomerAddress(customerId, addressId, payload)
      : this.customerService.createCustomerAddress(customerId, payload);

    request$
      .pipe(finalize(() => this.relationsLoading.set(false)))
      .subscribe({
        next: (response) => {
          if (response.ok) {
            this.updateAddressList(response.body);
            this.addressModalShown.set(false);
            setTimeout(() => this.cancelAddressEdit(), 200);
            this.toastr.success('Dirección guardada correctamente');
          } else {
            this.setError(response.message);
            this.toastr.error(response.message);
          }
        },
        error: (err) => {
          this.setError(err.error?.message);
          console.error('Error saving address:', err);
        }
      });
  }

  /** RFC siempre en mayúsculas mientras se escribe. */
  onTaxIdInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const upper = input.value.toUpperCase().slice(0, this.maxLength.taxId);
    if (input.value !== upper) {
      input.value = upper;
    }
    this.customerForm.get('taxId')?.setValue(upper, { emitEvent: false });
  }

  /** Solo dígitos en el teléfono del cliente (máx. 10). */
  onPhoneInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '').slice(0, this.maxLength.phone);
    if (input.value !== digits) {
      input.value = digits;
    }
    this.customerForm.get('phone')?.setValue(digits, { emitEvent: false });
  }

  /** Solo dígitos en el teléfono de contacto (hasta contactPhoneMasked). */
  onContactPhoneInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '').slice(0, this.maxLength.contactPhoneMasked);
    if (input.value !== digits) {
      input.value = digits;
    }
    this.contactForm.get('contactPhone')?.setValue(digits, { emitEvent: false });
  }

  /** Solo 5 dígitos en el código postal. */
  onZipCodeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '').slice(0, this.maxLength.zipCode);
    if (input.value !== digits) {
      input.value = digits;
    }
    this.addressForm.get('zipCode')?.setValue(digits, { emitEvent: false });
  }

  /** Consulta CP (solo México) y precarga municipio/colonias. */
  onZipCodeBlur(): void {
    if (!this.isMexicoSelected()) {
      return;
    }
    const zipCode = String(this.addressForm.get('zipCode')?.value ?? '').trim();
    if (!/^\d{5}$/.test(zipCode)) {
      return;
    }

    this.postalCodeLoading.set(true);
    this.addressService.getByZipCode(zipCode).pipe(
      finalize(() => {
        this.postalCodeLoading.set(false);
        this.cdr.markForCheck();
      })
    ).subscribe({
      next: (response) => {
        const data = response.body;
        if (!response.ok || !data) {
          return;
        }

        const neighborhoods = (data.neighborhoods ?? [])
          .map((n) => ({ label: n.name ?? '', value: n.name ?? '' }))
          .filter((n) => n.value);
        this.coloniaOptions.set(neighborhoods);

        const currentColonia = this.addressForm.get('colonia')?.value as string;
        const colonia =
          neighborhoods.length === 1
            ? neighborhoods[0].value
            : neighborhoods.some((n) => n.value === currentColonia)
              ? currentColonia
              : '';

        this.addressForm.patchValue({
          municipality: data.municipality ?? '',
          colonia
        });
        this.refreshMunicipalityDisplay();
      },
      error: () => {
        // Sin resultados o error: no mostrar toast ni alterar el formulario.
      }
    });
  }

  saveContact(): void {
    const customerId = this.editingCustomerId();
    if (!customerId || this.contactForm.invalid) {
      this.contactForm.markAllAsTouched();
      this.cdr.markForCheck();
      return;
    }

    this.relationsLoading.set(true);
    this.clearError();
    const v = this.contactForm.value;
    const phoneDigits = typeof v.contactPhone === 'string'
      ? v.contactPhone.replace(/\D/g, '').slice(0, this.maxLength.contactPhone)
      : '';
    const payload: CreateCustomerContactRequest = {
      type: v.contactType,
      conactName: v.conactName,
      email: v.contactEmail || undefined,
      phone: phoneDigits || undefined
    };
    const contactId = this.editingContactId();
    const request$ = contactId
      ? this.customerService.updateCustomerContact(customerId, contactId, payload)
      : this.customerService.createCustomerContact(customerId, payload);

    request$.subscribe({
      next: (response) => {
        if (response.ok) {
          this.updateContactList(response.body);
          this.contactModalShown.set(false);
          setTimeout(() => this.cancelContactEdit(), 200);
        } else {
          this.setError(response.message || 'Error al guardar el contacto');
        }
        this.relationsLoading.set(false);
      },
      error: (err) => {
        this.setError(err.error?.message || 'Error al guardar el contacto');
        this.relationsLoading.set(false);
        console.error('Error saving contact:', err);
      }
    });
  }

  onSubmit(): void {
    if (this.customerForm.valid) {
      this.loading.set(true);
      this.error.set(null);

      const formValue = this.customerForm.value;
      const isEdit = this.isEditMode() && this.editingCustomerId() !== null;
      const taxId = String(formValue.taxId ?? '').trim();

      if (isEdit) {
        const request: UpdateCustomerRequest = {
          type: formValue.type || null,
          taxRegimeId: formValue.taxRegimeId ?? null,
          taxId: taxId || null,
          legalName: formValue.legalName,
          comercialName: formValue.comercialName,
          countryId: formValue.countryId ?? null,
          email: formValue.email,
          phone: formValue.phone || undefined,
          daysUntilExpiry: formValue.daysUntilExpiry ?? null,
          state: formValue.state || 'ACTIVE'
        };
        this.customerService.updateCustomer(this.editingCustomerId() as number, request)
          .pipe(finalize(() => this.loading.set(false)))
          .subscribe({ next: this.handleSaveSuccess(isEdit), error: this.handleSaveError(isEdit) });
      } else {
        const request: CreateCustomerRequest = {
          type: formValue.type || null,
          taxRegimeId: formValue.taxRegimeId ?? null,
          taxId: taxId || null,
          legalName: formValue.legalName,
          comercialName: formValue.comercialName,
          countryId: formValue.countryId ?? null,
          email: formValue.email,
          phone: formValue.phone || undefined,
          daysUntilExpiry: formValue.daysUntilExpiry ?? null
        };
        this.customerService.createCustomer(request)
          .pipe(finalize(() => this.loading.set(false)))
          .subscribe({ next: this.handleSaveSuccess(isEdit), error: this.handleSaveError(isEdit) });
      }
    } else {
      this.customerForm.markAllAsTouched();
    }
  }

  private handleSaveSuccess(isEdit: boolean) {
    return (response: { ok: boolean; body?: CustomerDTO }) => {
      if (response.ok && this.embedded()) {
        if (response.body) {
          this.saved.emit(response.body);
        }
        this.toastr.success(isEdit ? 'Cliente actualizado correctamente' : 'Cliente creado correctamente');
        return;
      }
      if (response.ok) {
        const customerId = response.body?.id;
        if (customerId) {
          this.isEditMode.set(true);
          this.editingCustomerId.set(customerId);
          if (response.body) {
            this.patchCustomer(response.body);
          }
          this.router.navigate(['/customers', customerId], { replaceUrl: true });
          this.refreshRelations();
        }
        this.toastr.success(isEdit ? 'Cliente actualizado correctamente' : 'Cliente creado correctamente');
      } else {
        this.setError(isEdit ? 'Error al actualizar el cliente' : 'Error al crear el cliente');
      }
    };
  }

  private handleSaveError(isEdit: boolean) {
    return (err: { error?: { message?: string } }) => {
      this.setError(err.error?.message || 'Error al conectar con el servidor');
      console.error(isEdit ? 'Error updating customer:' : 'Error creating customer:', err);
    };
  }

  cancel(): void {
    if (this.embedded()) {
      this.dismissed.emit();
      return;
    }
    this.router.navigate(['/customers']);
  }

  readonly statusTooltip = 'Estado del cliente: Activo o Inactivo. Puedes reactivar un cliente inactivo.';

  isInactiveCustomer(): boolean {
    return this.customerForm.get('state')?.value === 'INACTIVE';
  }

  statusDisplayLabel(): string {
    const state = this.customerForm.get('state')?.value;
    return this.stateOptions.find((o) => o.value === state)?.label ?? (state || '—');
  }

  /** Reactiva un cliente inactivo enviando state=ACTIVE con el resto de datos actuales. */
  reactivateCustomer(): void {
    const customerId = this.editingCustomerId();
    if (customerId == null || !this.isInactiveCustomer() || this.reactivating() || this.loading()) {
      return;
    }
    if (this.customerForm.invalid) {
      this.customerForm.markAllAsTouched();
      this.setError('Corrige los datos del formulario antes de reactivar el cliente.');
      return;
    }

    const formValue = this.customerForm.value;
    const taxId = String(formValue.taxId ?? '').trim();

    this.reactivating.set(true);
    this.clearError();
    const request: UpdateCustomerRequest = {
      type: formValue.type || null,
      taxRegimeId: formValue.taxRegimeId ?? null,
      taxId: taxId || null,
      legalName: formValue.legalName,
      comercialName: formValue.comercialName,
      countryId: formValue.countryId ?? null,
      email: formValue.email,
      phone: formValue.phone || undefined,
      daysUntilExpiry: formValue.daysUntilExpiry ?? null,
      state: 'ACTIVE'
    };

    this.customerService.updateCustomer(customerId, request)
      .pipe(finalize(() => this.reactivating.set(false)))
      .subscribe({
        next: (response) => {
          if (response.ok && response.body) {
            this.patchCustomer(response.body);
            this.toastr.success('Cliente reactivado correctamente');
            this.cdr.markForCheck();
          } else {
            this.setError(response.message || 'Error al reactivar el cliente');
          }
        },
        error: (err) => {
          this.setError(err.error?.message || 'Error al conectar con el servidor');
          console.error('Error reactivating customer:', err);
        }
      });
  }

  daysUntilExpiryPlaceholder(): string {
    const days = this.companyDefaultDaysUntilExpiry();
    if (days != null) {
      return `${days} días por defecto de la compañía`;
    }
    return 'Sin plazo por defecto de la compañía';
  }

  onTabChange(value: string | number | undefined): void {
    const tab = String(value ?? 'generales');
    this.activeTab.set(tab);
    if (tab === 'adjuntos') {
      const customerId = this.editingCustomerId();
      if (customerId) {
        this.loadAttachments(customerId);
      }
    }
  }

  /** Recarga el cliente desde el API (botón refresh del header). */
  refreshCustomer(): void {
    const id = this.editingCustomerId();
    if (id == null || this.loading()) return;
    this.loadCompanyDefaultPaymentTerms();
    this.loadCustomer(id);
    if (this.activeTab() === 'adjuntos') {
      this.loadAttachments(id);
    }
  }

  private loadCompanyDefaultPaymentTerms(): void {
    this.companyService
      .getMyCompany()
      .pipe(catchError(() => of({ ok: false, body: null })))
      .subscribe((response) => {
        if (response.ok !== false && response.body) {
          this.companyDefaultDaysUntilExpiry.set(response.body.daysUntilExpiry ?? null);
          this.cdr.markForCheck();
        }
      });
  }

  private loadCustomer(customerId: number): void {
    this.loading.set(true);
    this.formDataLoading.set(true);
    this.clearError();
    this.customerService.getCustomerById(customerId).pipe(
      finalize(() => {
        this.loading.set(false);
        this.formDataLoading.set(false);
      })
    ).subscribe({
      next: (response) => {
        if (response.ok) {
          this.patchCustomer(response.body);
          this.addresses.set(response.body.addresses || []);
          this.contacts.set(response.body.contacts || []);
        } else {
          this.setError(response.message || 'Error al cargar el cliente');
        }
      },
      error: (err) => {
        this.setError('Error al cargar el cliente. Verifica que el API esté ejecutándose.');
        console.error('Error loading customer for edit:', err);
      }
    });
  }

  private patchCustomer(customer: CustomerDTO): void {
    const taxRegime = (customer as { taxRegime?: TaxRegimeDTO }).taxRegime;
    this.customerForm.patchValue({
      type: customer.type,
      taxRegimeId: taxRegime?.id ?? null,
      taxId: customer.taxId,
      legalName: customer.legalName,
      comercialName: customer.comercialName,
      countryId: customer.country?.id ?? null,
      email: customer.email || '',
      phone: customer.phone || '',
      state: customer.state || 'ACTIVE',
      daysUntilExpiry: customer.daysUntilExpiry ?? null
    });
  }

  private refreshRelations(): void {
    const customerId = this.editingCustomerId();
    if (!customerId) return;
    this.customerService.getCustomerById(customerId).subscribe({
      next: (response) => {
        if (response.ok) {
          this.addresses.set(response.body.addresses || []);
          this.contacts.set(response.body.contacts || []);
          if (this.activeTab() === 'adjuntos') {
            this.loadAttachments(customerId);
          }
        }
      }
    });
  }

  private updateAddressList(address: CustomerAddressDTO): void {
    this.addresses.update((items) => {
      const idx = items.findIndex((item) => item.id === address.id);
      if (idx >= 0) {
        const next = [...items];
        next[idx] = address;
        return next;
      }
      return [...items, address];
    });
  }

  private updateContactList(contact: CustomerContactDTO): void {
    this.contacts.update((items) => {
      const idx = items.findIndex((item) => item.id === contact.id);
      if (idx >= 0) {
        const next = [...items];
        next[idx] = contact;
        return next;
      }
      return [...items, contact];
    });
  }

  private loadAttachments(customerId: number): void {
    this.attachmentsLoading.set(true);
    this.customerService.listCustomerAttachments(customerId).pipe(
      finalize(() => {
        this.attachmentsLoading.set(false);
        this.cdr.markForCheck();
      })
    ).subscribe({
      next: (response) => {
        if (response.ok) {
          this.attachments.set(response.body ?? []);
        } else {
          this.toastr.error(response.message ?? 'Error al cargar adjuntos');
        }
      },
      error: () => this.toastr.error('Error al cargar adjuntos')
    });
  }

  async onAttachmentSelect(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    const customerId = this.editingCustomerId();
    if (!customerId || files.length === 0) {
      return;
    }

    const MAX_SIZE_MB = 10;
    const maxBytes = MAX_SIZE_MB * 1024 * 1024;
    this.uploadingAttachments.set(true);
    let uploaded = 0;

    try {
      for (const file of files) {
        if (file.size > maxBytes) {
          this.toastr.error(`${file.name} supera ${MAX_SIZE_MB} MB`);
          continue;
        }
        const contentBase64 = await this.readFileAsBase64(file);
        const response = await new Promise<{ ok?: boolean; message?: string; body?: ReadFileResponse }>((resolve, reject) => {
          this.customerService.attachCustomerFile(customerId, {
            name: file.name,
            contentType: file.type || 'application/octet-stream',
            contentBase64
          }).subscribe({ next: resolve, error: reject });
        });
        if (response.ok && response.body) {
          uploaded += 1;
        } else {
          this.toastr.error(response.message ?? `Error al subir ${file.name}`);
        }
      }
      if (uploaded > 0) {
        this.toastr.success(uploaded === 1 ? 'Archivo adjuntado correctamente' : `${uploaded} archivos adjuntados`);
        this.loadAttachments(customerId);
      }
    } catch (err: unknown) {
      const message = (err as { error?: { message?: string } })?.error?.message ?? 'Error al subir el archivo';
      this.toastr.error(message);
    } finally {
      this.uploadingAttachments.set(false);
      this.cdr.markForCheck();
    }
  }

  openAttachment(file: ReadFileResponse): void {
    if (!file.content) {
      this.toastr.error('No hay URL de descarga para este archivo');
      return;
    }
    window.open(file.content, '_blank', 'noopener');
  }

  formatFileSize(size?: number): string {
    if (size == null || !Number.isFinite(size)) {
      return '-';
    }
    if (size < 1024) {
      return `${size} B`;
    }
    if (size < 1024 * 1024) {
      return `${(size / 1024).toFixed(1)} KB`;
    }
    return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  }

  getFileIcon(contentType?: string): string {
    const type = (contentType ?? '').toLowerCase();
    if (type.startsWith('image/')) return 'pi-image';
    if (type === 'application/pdf') return 'pi-file-pdf';
    if (type.includes('spreadsheet') || type.includes('excel')) return 'pi-file-excel';
    if (type.includes('word') || type.includes('msword')) return 'pi-file-word';
    return 'pi-file';
  }

  private readFileAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.includes(',') ? result.split(',')[1] : result;
        resolve(base64 ?? '');
      };
      reader.onerror = () => reject(reader.error ?? new Error('No se pudo leer el archivo'));
      reader.readAsDataURL(file);
    });
  }

  private clearError(): void {
    this.error.set(null);
  }

  private setError(message: string): void {
    this.error.set(message);
    this.toastr.error(message);
  }
}
